import React from 'react';
import classnames from 'classnames';
import { Icon, IconProps } from '../icon';
import { isTextLinkProps } from '../../utils/is-link-props';
import { Size } from '../types';

import styles from './link.module.scss';

const textLinkClass = 'text-link';
const iconLinkClass = 'icon-link';

type HTMLAnchorDefaultProps = { href: string } & Pick<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  'target' | 'rel' | 'onClick' | 'className' | 'title' | 'onContextMenu' | 'onKeyDown'
>;

export type TextLinkProps = {
  variant:
    | 'button-primary'
    | 'button-primary-lime'
    | 'button-secondary'
    | 'button-secondary-on-media'
    | 'button-ghost'
    | 'hyperlink-standalone'
    | 'button-primary-brabus'
    // Hyperlink [HYP]
    | 'HYP-100'
    | 'HYP-200'
    | 'HYP-LGL'
    // Depreciated
    | 'hyperlink-100'
    | 'hyperlink-200'
    | 'hyperlink-legal';
  linkText: string;
  size?: Size;
  icon?: IconProps;
  iconPosition?: 'left' | 'right';
  isExternal?: boolean;
  'aria-label'?: string;
  role?: string;
  tabIndex?: number;
  'aria-current'?: boolean | 'time' | 'false' | 'true' | 'page' | 'step' | 'location' | 'date';
  'aria-haspopup'?: boolean;
  'aria-expanded'?: boolean;
  linkTag?: string;
  linkTagColor?: 'grey' | 'lime';
} & HTMLAnchorDefaultProps;

export type IconLinkProps = {
  variant:
    | 'hyperlink'
    | 'button-primary'
    | 'button-secondary'
    | 'button-ghost'
    | 'button-secondary-on-media'
    | 'button-primary-brabus';
  icon: IconProps;
  tabIndex?: number;
  'aria-label': string;
  size?: Size;
} & HTMLAnchorDefaultProps;

export type LinkProps = TextLinkProps | IconLinkProps;

export const TextLink = React.forwardRef<HTMLAnchorElement, TextLinkProps>(
  (
    {
      href,
      target,
      rel,
      onClick,
      title,
      linkText,
      className,
      icon,
      variant,
      size,
      isExternal,
      iconPosition = 'left',
      onContextMenu,
      'aria-label': ariaLabel,
      onKeyDown,
      role,
      tabIndex = 0,
      'aria-current': ariaCurrent,
      'aria-haspopup': ariaHaspopup,
      'aria-expanded': ariaExpanded,
      linkTag,
      linkTagColor = 'grey'
    },
    ref
  ) => {
    const modifiedIconPosition = isExternal ? 'right' : iconPosition;

    const chooseIcon = () => {
      if (isExternal) {
        return <Icon {...icon} name="external-link--small" className={styles[`${textLinkClass}__icon`]} aria-hidden />;
      } else if (icon) {
        return <Icon {...icon} className={styles[`${textLinkClass}__icon`]} aria-hidden />;
      }
    };

    const navTag = () => {
      if (linkTag) {
        return (
          <span
            className={classnames(styles[`${textLinkClass}__tag--${linkTagColor}`], styles[`${textLinkClass}__tag`])}
          >
            <i>·</i>
            {linkTag}
          </span>
        );
      }
    };

    const classes = classnames(
      styles[textLinkClass],
      { [styles[`${textLinkClass}--${variant}`]]: variant },
      {
        [styles[`${textLinkClass}--button`]]: [
          'button-primary',
          'button-primary-lime',
          'button-primary-brabus',
          'button-secondary',
          'button-ghost',
          'button-secondary-on-media'
        ].includes(variant)
      },
      { [styles[`${textLinkClass}--icon-${modifiedIconPosition}`]]: modifiedIconPosition },
      { [styles[`${textLinkClass}--${size}`]]: size },
      className
    );

    return (
      <a
        href={href}
        target={target}
        className={classes}
        rel={rel}
        onClick={onClick}
        title={title}
        data-testid="text-link-component"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        role={role}
        tabIndex={tabIndex}
        aria-current={ariaCurrent}
        aria-haspopup={ariaHaspopup}
        aria-expanded={ariaExpanded}
        onContextMenu={onContextMenu}
        ref={ref}
      >
        <span className={styles[`${textLinkClass}__text`]}>
          {linkText}
          {navTag()}
        </span>
        {chooseIcon()}
      </a>
    );
  }
);

export const IconLink: React.FC<IconLinkProps> = ({
  href,
  target,
  rel,
  onClick,
  className,
  icon,
  tabIndex = 0,
  variant = 'button-primary',
  size = '300',
  'aria-label': ariaLabel,
  onContextMenu
}) => {
  const classes = classnames(
    styles[iconLinkClass],
    { [styles[`${iconLinkClass}--${variant}`]]: variant },
    {
      [styles[`${iconLinkClass}--button`]]: [
        'button-primary',
        'button-primary-brabus',
        'button-secondary',
        'button-ghost',
        'button-secondary-on-media'
      ].includes(variant)
    },
    { [styles[`${iconLinkClass}--${size}`]]: size },
    className
  );

  return (
    <a
      href={href}
      target={target}
      className={classes}
      rel={rel}
      onClick={onClick}
      tabIndex={tabIndex}
      aria-label={ariaLabel}
      onContextMenu={onContextMenu}
    >
      <Icon {...icon} className={styles[`${iconLinkClass}__icon`]} />
    </a>
  );
};

/**
 * The Link component is typed by a union of TextLinkProps or IconLinkProps. The props you give it determine if its a IconLink
 * or a TextLink component. While you can use TextLink or IconLink directly we would recommend to use Link.
 *
 * Not adding children will result in an IconLink. Using the icon prop makes the aria-label prop mandatory.
 * Using children will make the icon prop optional and will result in an TextLink.
 */

export const Link: React.FC<LinkProps> = (props: LinkProps) => {
  if (isTextLinkProps(props)) {
    return <TextLink {...props} />;
  }

  return <IconLink {...props} />;
};

TextLink.displayName = 'TextLink';
